if (!customElements.get('bioluma-nutrients-carousel')) {
  customElements.define(
    'bioluma-nutrients-carousel',
    class BiolumaNutrientsCarousel extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        this.wrap = this.querySelector('[data-nutrients-track-wrap]');
        this.cards = Array.from(this.querySelectorAll('[data-nutrient-card]'));
        this.dots = Array.from(this.querySelectorAll('[data-nutrient-dot]'));
        this.prevBtn = this.querySelector('[data-nutrients-prev]');
        this.nextBtn = this.querySelector('[data-nutrients-next]');
        this.selected = 0;
        this.drag = { active: false, startX: 0, startScroll: 0, moved: false };
        this.isScrolling = false;
        this.scrollEndTimer = null;
        this.justDragged = false;
        this.isVisible = false;
        this.observer = null;

        this.onMouseDown = (event) => {
          if (event.button !== 0 || !this.wrap) return;
          this.drag = {
            active: true,
            startX: event.pageX,
            startScroll: this.wrap.scrollLeft,
            moved: false,
          };
          this.wrap.classList.add('is-dragging');
          this.wrap.style.scrollSnapType = 'none';
          this.wrap.style.setProperty('-webkit-scroll-snap-type', 'none');
        };

        this.onMouseMove = (event) => {
          if (!this.drag.active || !this.wrap) return;
          const delta = event.pageX - this.drag.startX;
          if (Math.abs(delta) > 5) this.drag.moved = true;
          this.wrap.scrollLeft = this.drag.startScroll - delta;
        };

        this.onMouseUp = () => {
          if (!this.drag.active) return;
          const hadDrag = this.drag.moved;
          this.drag.active = false;
          this.wrap?.classList.remove('is-dragging');
          this.wrap.style.scrollSnapType = '';
          this.wrap.style.removeProperty('-webkit-scroll-snap-type');
          if (hadDrag) this.justDragged = true;
          this.finishScrollInteraction();
        };

        this.onScrollEnd = () => this.finishScrollInteraction();

        this.onScroll = () => {
          if (this.drag.active) return;
          this.isScrolling = true;
          window.clearTimeout(this.scrollEndTimer);
          this.scrollEndTimer = window.setTimeout(() => this.finishScrollInteraction(), 120);
        };

        this.wrap?.addEventListener('mousedown', this.onMouseDown);
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onMouseUp);
        this.wrap?.addEventListener('scroll', this.onScroll, { passive: true });
        this.wrap?.addEventListener('scrollend', this.onScrollEnd);
        this.wrap?.addEventListener('touchend', this.onScrollEnd, { passive: true });

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const index = Number(dot.dataset.nutrientDot);
            if (Number.isFinite(index)) this.goTo(index);
          });
        });

        this.prevBtn?.addEventListener('click', () => {
          if (this.selected > 0) this.goTo(this.selected - 1);
        });

        this.nextBtn?.addEventListener('click', () => {
          if (this.selected < this.cards.length - 1) this.goTo(this.selected + 1);
        });

        this.cards.forEach((card, index) => {
          card.addEventListener('mouseenter', () => {
            if (!this.drag.active && !this.isScrolling) this.select(index, false);
          });
          card.addEventListener('focus', () => {
            if (!this.drag.active && !this.isScrolling) this.select(index, false);
          });
        });

        this.cards.forEach((card) => {
          const video = card.querySelector('video');
          if (video) this.prepareVideo(video);
        });

        // iOS: desbloqueia playback no primeiro toque da seção
        this.onFirstGesture = () => {
          this.playVideo(this.selected);
        };
        this.addEventListener('touchstart', this.onFirstGesture, { once: true, passive: true });
        this.addEventListener('click', this.onFirstGesture, { once: true });

        this.select(0, false);
        this.updateNavState();
        this.setupVisibilityObserver();
      }

      prepareVideo(video) {
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.volume = 0;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('autoplay', '');
        video.setAttribute('preload', 'auto');
        video.preload = 'auto';

        // Garante source type=video/mp4 (iOS)
        const source = video.querySelector('source');
        if (source && !source.getAttribute('type')) {
          source.setAttribute('type', 'video/mp4');
        }
      }

      playVideo(index) {
        const video = this.cards[index]?.querySelector('video');
        if (!video) return;

        this.prepareVideo(video);

        // Pausa os outros
        this.cards.forEach((card, i) => {
          if (i === index) return;
          const other = card.querySelector('video');
          if (!other) return;
          other.pause();
          other.removeAttribute('autoplay');
        });

        let attempts = 0;
        const start = () => {
          attempts += 1;
          const playPromise = video.play();
          if (!playPromise || typeof playPromise.catch !== 'function') return;
          playPromise.catch(() => {
            if (attempts > 3) return;
            window.setTimeout(() => {
              try {
                video.load();
              } catch (e) {
                /* ignore */
              }
              video.addEventListener('canplay', start, { once: true });
            }, 80 * attempts);
          });
        };

        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          start();
          return;
        }

        video.addEventListener('loadeddata', start, { once: true });
        video.addEventListener('canplay', start, { once: true });
        video.addEventListener('canplaythrough', start, { once: true });

        if (video.readyState === 0 || video.networkState === HTMLMediaElement.NETWORK_EMPTY) {
          try {
            video.load();
          } catch (e) {
            /* ignore */
          }
        } else {
          start();
        }
      }

      setupVisibilityObserver() {
        const target = this.closest('.bioluma-nutrients') || this;

        if (!('IntersectionObserver' in window)) {
          this.onSectionVisible();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.onSectionVisible();
              else this.onSectionHidden();
            });
          },
          { threshold: 0.12, rootMargin: '80px 0px' }
        );

        this.observer.observe(target);

        const checkNow = () => {
          const rect = target.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            this.onSectionVisible();
          }
        };

        requestAnimationFrame(() => requestAnimationFrame(checkNow));
        window.setTimeout(checkNow, 150);
      }

      preloadVisibleCards() {
        if (!this.wrap) return;

        const wrapRect = this.wrap.getBoundingClientRect();

        this.cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const inCarousel =
            cardRect.right > wrapRect.left + 8 && cardRect.left < wrapRect.right - 8;
          const shouldLoad = inCarousel || index === this.selected;

          if (!shouldLoad) return;

          const video = card.querySelector('video');
          if (!video || video.preload === 'auto') return;

          video.preload = 'auto';
          if (video.readyState === 0) video.load();
        });
      }

      onSectionVisible() {
        if (this.isVisible) return;
        this.isVisible = true;
        this.preloadVisibleCards();
        this.playVideo(this.selected);
      }

      onSectionHidden() {
        this.isVisible = false;
        this.pauseAllVideos();
      }

      disconnectedCallback() {
        window.clearTimeout(this.scrollEndTimer);
        this.observer?.disconnect();
        this.wrap?.removeEventListener('mousedown', this.onMouseDown);
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onMouseUp);
        this.wrap?.removeEventListener('scroll', this.onScroll);
        this.wrap?.removeEventListener('scrollend', this.onScrollEnd);
        this.bound = false;
      }

      goTo(index) {
        this.select(index, true);
      }

      select(index, scrollIntoView = true) {
        if (!this.cards.length) return;
        const next = Math.max(0, Math.min(index, this.cards.length - 1));
        if (next === this.selected && !scrollIntoView) {
          if (this.isVisible) this.playVideo(next);
          return;
        }

        this.pauseVideo(this.selected);
        this.selected = next;

        this.cards.forEach((card, i) => {
          card.classList.toggle('is-selected', i === next);
        });

        this.dots.forEach((dot) => {
          const active = Number(dot.dataset.nutrientDot) === next;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        if (scrollIntoView) {
          this.scrollCardIntoView(next);
        }

        this.preloadVisibleCards();
        if (this.isVisible) this.playVideo(next);
        this.updateNavState();
      }

      updateNavState() {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.selected <= 0;
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = this.selected >= this.cards.length - 1;
        }
      }

      scrollCardIntoView(index) {
        const card = this.cards[index];
        if (!card || !this.wrap) return;

        const target =
          card.offsetLeft - (this.wrap.clientWidth - card.offsetWidth) / 2;

        this.wrap.scrollTo({
          left: Math.max(0, target),
          behavior: 'smooth',
        });
      }

      finishScrollInteraction() {
        this.isScrolling = false;
        window.clearTimeout(this.scrollEndTimer);

        const isDesktop = window.matchMedia('(min-width: 960px)').matches;
        if (isDesktop && !this.justDragged) return;

        this.justDragged = false;
        this.syncFromScroll();
      }

      syncFromScroll() {
        if (!this.wrap || !this.cards.length) return;

        const center = this.wrap.scrollLeft + this.wrap.clientWidth / 2;
        let closest = 0;
        let min = Infinity;

        this.cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const dist = Math.abs(center - cardCenter);
          if (dist < min) {
            min = dist;
            closest = index;
          }
        });

        if (closest !== this.selected) {
          this.pauseVideo(this.selected);
          this.selected = closest;
          this.cards.forEach((card, i) => card.classList.toggle('is-selected', i === closest));
          this.dots.forEach((dot) => {
            const active = Number(dot.dataset.nutrientDot) === closest;
            dot.classList.toggle('is-active', active);
            dot.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          this.preloadVisibleCards();
          if (this.isVisible) this.playVideo(closest);
          this.updateNavState();
        }
      }

      pauseAllVideos() {
        this.cards.forEach((_, index) => this.pauseVideo(index));
      }

      pauseVideo(index) {
        const video = this.cards[index]?.querySelector('video');
        if (!video) return;
        video.pause();
      }
    }
  );
}
